import { useCallback, useEffect, useState } from "react";

/**
 * Hook for per-user column ordering, persisted in localStorage.
 *
 * - `togglableKeys` is the canonical list of reorderable keys.
 * - On load, the saved order is filtered to known keys (drops removed/renamed
 *   ones), and any new togglable key not present is appended at the end.
 * - `reorder(activeKey, overKey)` moves `activeKey` to the position of
 *   `overKey` (the "drop on" semantics dnd-kit uses by default).
 */
export interface UseColumnOrderOptions {
  storageKey: string;
  togglableKeys: string[];
}

export function useColumnOrder({
  storageKey,
  togglableKeys,
}: UseColumnOrderOptions) {
  const [order, setOrder] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return [...togglableKeys];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [...togglableKeys];
      const validSaved = parsed.filter(
        (k: any) => typeof k === "string" && togglableKeys.includes(k),
      );
      const missing = togglableKeys.filter((k) => !validSaved.includes(k));
      return [...validSaved, ...missing];
    } catch {
      return [...togglableKeys];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(order));
    } catch {
      // ignore quota / private mode errors
    }
  }, [storageKey, order]);

  const reorder = useCallback((activeKey: string, overKey: string) => {
    if (activeKey === overKey) return;
    setOrder((prev) => {
      const fromIdx = prev.indexOf(activeKey);
      const toIdx = prev.indexOf(overKey);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const next = [...prev];
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, activeKey);
      return next;
    });
  }, []);

  return { order, setOrder, reorder };
}
